import { formatPrice } from '@/utils/format';
import { lineBreakdown } from '@/utils/pricing';
import styles from './PriceBreakdown.module.css';

/**
 * What one line on a receipt is made of: the base edition, then each language
 * adaptation bought alongside it, each with its own price.
 *
 * A book's price is not one number — it is the base language plus whatever
 * translations were chosen — and a receipt showing only the sum leaves the buyer
 * unable to check what they were charged for. Rendered wherever a priced line is
 * listed (cart summary, confirmation, purchase history) so the make-up reads the
 * same before and after paying.
 *
 * Falls back to naming the languages without prices for orders placed before
 * lines carried their make-up: those have a total and nothing to break it into.
 */
export function PriceBreakdown({ item, currency }) {
  const { basePrice, charges, unit, qty, itemised } = lineBreakdown(item);
  const languages = item.languages ?? [];
  const code = item.currency ?? currency;

  if (!itemised || charges.length === 0) {
    return languages.length > 0 ? (
      <div className={styles.plain}>+ {languages.join(', ')}</div>
    ) : null;
  }

  return (
    <div className={styles.breakdown}>
      {/* A language added to a copy already owned has no base edition to price:
          the reader has it. Only the languages, at their part-term price. */}
      {item.kind !== 'add_languages' && (
        <Row label={item.language ? `Base · ${item.language}` : 'Base price'}>
          {formatPrice(basePrice, code)}
        </Row>
      )}

      {charges.map((charge) => (
        <Row key={charge.language} label={`+ ${charge.language}`}>
          {formatPrice(charge.price, code)}
        </Row>
      ))}

      {/* Only worth spelling out when the line isn't a single copy — otherwise
          the unit price and the line total are the same number said twice. */}
      {qty > 1 && (
        <Row label={`${formatPrice(unit, code)} × ${qty}`}>{formatPrice(unit * qty, code)}</Row>
      )}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.amount}>{children}</span>
    </div>
  );
}
